import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform, type MotionProps } from 'framer-motion';
import { ButtonLink } from '../ui';
import { DURATION, EASE_OUT_EXPO, splitWords, wordReveal } from '../../lib/motion';

const MotionLink = motion.create(Link);

const HEADLINE = 'Путешествие, собранное до последнего поворота';

/**
 * Hero.
 *
 * Three layers move at different rates as the page scrolls — aurora, headline,
 * preview cards — which is what sells depth without a single image download.
 * All of it collapses to a static layout under `prefers-reduced-motion`.
 */
export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  const auroraY = useTransform(scrollYProgress, [0, 1], ['0%', '32%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const cardsY = useTransform(scrollYProgress, [0, 1], ['0%', '-14%']);

  const words = splitWords(HEADLINE);

  return (
    <section
      ref={ref}
      className="grain relative isolate flex min-h-[100svh] items-center overflow-hidden pt-24 pb-16"
    >
      <motion.div style={reduced ? undefined : { y: auroraY }} className="absolute inset-0 -z-10">
        <div className="aurora" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.18] mix-blend-multiply dark:opacity-[0.24] dark:mix-blend-screen"
          style={{ backgroundImage: "url('/assets/hero-coastal-road.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--surface)]" />
      </motion.div>

      <div className="container-page">
        <motion.div
          style={reduced ? undefined : { y: contentY, opacity: contentOpacity }}
          className="grid items-center gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)]"
        >
          <div className="max-w-2xl">
            <MotionLink
              to="/deals"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: DURATION.base, ease: EASE_OUT_EXPO }}
              className="group mb-7 inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-raised)]/70 py-1.5 pl-1.5 pr-4 text-sm backdrop-blur transition-colors hover:bg-[var(--surface-raised)]"
            >
              <span className="rounded-full bg-clay-500 px-2.5 py-1 text-xs font-semibold text-white">
                Новое
              </span>
              <span className="text-[var(--text-muted)] transition-colors group-hover:text-[var(--text-strong)]">
                🔥 Горячие туры и дешёвые билеты
              </span>
              <span
                aria-hidden
                className="text-[var(--text-muted)] transition-transform duration-200 motion-safe:group-hover:translate-x-0.5"
              >
                →
              </span>
            </MotionLink>

            {/* Words animate individually, but real space text nodes stay
                between them so the headline copies and reads as one sentence. */}
            <h1 className="display text-[clamp(2.75rem,7vw,5.25rem)]">
              {words.map((word, index) => (
                <span key={`${word}-${index}`}>
                  <motion.span
                    className="inline-block"
                    initial={reduced ? undefined : 'hidden'}
                    animate={reduced ? undefined : 'visible'}
                    variants={wordReveal}
                    transition={{ delay: 0.08 + index * 0.055 }}
                    style={{ transformOrigin: 'bottom' }}
                  >
                    {index === words.length - 1 ? (
                      <span className="bg-gradient-to-r from-clay-500 via-sun-500 to-lagoon-500 bg-clip-text text-transparent">
                        {word}
                      </span>
                    ) : (
                      word
                    )}
                  </motion.span>
                  {index < words.length - 1 ? ' ' : null}
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: DURATION.base, ease: EASE_OUT_EXPO, delay: 0.55 }}
              className="mt-7 max-w-xl text-lg leading-relaxed text-[var(--text-muted)] sm:text-xl"
            >
              Travessa собирает маршрут по дням, точки на карте, брони и фотографии в один живой
              документ. Подберите выгодный тур или билет, спланируйте сами, попросите AI — или
              возьмите за основу чужое путешествие, которое уже проверено дорогой.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: DURATION.base, ease: EASE_OUT_EXPO, delay: 0.68 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <ButtonLink to="/register" size="lg">
                Начать планировать
              </ButtonLink>
              <ButtonLink to="/gallery" variant="outline" size="lg">
                Посмотреть маршруты
              </ButtonLink>
            </motion.div>

            <motion.dl
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: DURATION.slow, delay: 0.9 }}
              className="mt-12 flex flex-wrap gap-x-10 gap-y-5"
            >
              {[
                { value: 'День за днём', label: 'таймлайн с временем и местами' },
                { value: 'Гео-фото', label: 'сами встают на нужные точки' },
                { value: 'Без ПД', label: 'регистрация без личных данных' },
              ].map((stat) => (
                <div key={stat.value}>
                  <dt className="text-[0.95rem] font-semibold">{stat.value}</dt>
                  <dd className="text-sm text-[var(--text-muted)]">{stat.label}</dd>
                </div>
              ))}
            </motion.dl>
          </div>

          <motion.div
            style={reduced ? undefined : { y: cardsY }}
            className="relative hidden h-[520px] lg:block"
          >
            <PreviewCards />
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#how"
        aria-label="Листайте вниз"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: DURATION.slow }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-xs text-[var(--text-muted)] md:flex"
      >
        <span className="eyebrow">Листайте</span>
        <motion.span
          animate={reduced ? undefined : { y: [0, 7, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="block h-8 w-px bg-gradient-to-b from-[var(--text-muted)] to-transparent"
        />
      </motion.a>
    </section>
  );
}

/**
 * Floating product previews. Deliberately drawn in markup rather than shipped as
 * screenshots: they stay sharp on every display, theme-aware, and never go stale
 * when the real UI changes.
 */
function PreviewCards() {
  const reduced = useReducedMotion();
  const float = (delay: number) =>
    reduced
      ? {}
      : ({
          animate: { y: [0, -12, 0] },
          transition: {
            duration: 6 + delay,
            repeat: Infinity,
            ease: 'easeInOut' as const,
            delay,
          },
        } satisfies MotionProps);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 40, rotate: -4 }}
        animate={{ opacity: 1, y: 0, rotate: -4 }}
        transition={{ duration: DURATION.slow, ease: EASE_OUT_EXPO, delay: 0.4 }}
        className="absolute left-0 top-4 w-[19rem]"
      >
        <motion.div {...float(0)} className="surface-card overflow-hidden p-0">
          <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-4 py-3">
            <span className="text-sm font-semibold">День 5 · Каякинг над Кековой</span>
            <span className="text-xs text-[var(--text-muted)]">6 июня</span>
          </div>
          <ul className="divide-y divide-[var(--border-subtle)]">
            {[
              ['08:30', '🛶', 'Морской каякинг над затонувшим городом'],
              ['15:30', '🏖️', 'Бухта Фырназ — снорклинг'],
              ['19:10', '🌄', 'Закат со смотровой на остров Мейс'],
            ].map(([time, icon, title]) => (
              <li key={title} className="flex items-start gap-3 px-4 py-3">
                <span className="w-11 shrink-0 pt-0.5 font-mono text-xs text-[var(--text-muted)]">
                  {time}
                </span>
                <span className="text-base leading-none">{icon}</span>
                <span className="text-sm leading-snug">{title}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 50, rotate: 5 }}
        animate={{ opacity: 1, y: 0, rotate: 5 }}
        transition={{ duration: DURATION.slow, ease: EASE_OUT_EXPO, delay: 0.6 }}
        className="absolute right-0 top-40 w-[17rem]"
      >
        <motion.div {...float(1.2)} className="surface-card overflow-hidden p-0">
          <div className="relative h-36 bg-gradient-to-br from-lagoon-400 via-lagoon-500 to-ink-800">
            <svg viewBox="0 0 240 140" className="absolute inset-0 size-full opacity-70">
              <path
                d="M14 118 C 60 96, 74 52, 118 46 S 196 62, 226 22"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeDasharray="5 6"
                strokeLinecap="round"
              />
              {[
                [14, 118],
                [72, 84],
                [118, 46],
                [176, 58],
                [226, 22],
              ].map(([cx, cy], i) => (
                <circle key={i} cx={cx} cy={cy} r={i === 2 ? 6 : 4} fill="white" />
              ))}
            </svg>
            <span className="absolute bottom-3 left-4 rounded-full bg-black/35 px-2.5 py-1 text-xs text-white backdrop-blur">
              34 точки на карте
            </span>
          </div>
          <div className="px-4 py-3">
            <div className="text-sm font-semibold">Ликийское побережье</div>
            <div className="mt-1 text-xs text-[var(--text-muted)]">
              11 ночей · 5 баз · 240 км на авто
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 60, rotate: -2 }}
        animate={{ opacity: 1, y: 0, rotate: -2 }}
        transition={{ duration: DURATION.slow, ease: EASE_OUT_EXPO, delay: 0.8 }}
        className="absolute bottom-2 left-12 w-[15rem]"
      >
        <motion.div {...float(2.4)} className="surface-card p-4">
          <div className="mb-3 flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <span className="size-1.5 rounded-full bg-emerald-500" />
            42 фото разложены автоматически
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {[
              ['/assets/mini-sea.png', 'Море у берега'],
              ['/assets/mini-village.png', 'Деревня по пути'],
              ['/assets/mini-balcony.png', 'Утро в поездке'],
              ['/assets/mini-path.png', 'Тропа к морю'],
              ['/assets/mini-kayak.png', 'Каяк в бухте'],
            ].map(([src, alt]) => (
              <img key={src} src={src} alt={alt} className="aspect-square rounded-md object-cover" />
            ))}
          </div>
        </motion.div>
      </motion.div>
    </>
  );
}
